import type { ProductVendor } from "@models/product";
import { vendorRowsForSku } from "@utils/vendorRows";

// §3.11 shelf-price rule: a cart line is backed by the LOWEST-priced vendor
// sku among active, in-stock entries OF THE SKU BEING BOUGHT. `vendors[]` is
// not filtered server-side (soft-deleted entries appear with isActive: false)
// and pre-Bug-#302 payloads omit isActive/inStock entirely — both are excluded
// by requiring the flags to be explicitly true.
//
// `selectedVariantId` narrows to one sku (#390): the selected variant's id, or
// null for the base, variant-less sku. It is a required parameter rather than
// an optional one so a new caller cannot silently opt out of the narrowing.
// The narrowing itself lives in `vendorRowsForSku` (shared with the admin
// surfaces).
//
// Today this changes nothing: the API sends only base rows in
// `product.vendors` and only that variant's rows in `variant.vendors`, so
// every array is already narrowed by construction. The guard is the
// PRECONDITION for widening those arrays to all of a product's supply rows —
// which the API deliberately has not done (see the Vendors note on
// GetProductResponse's mapping). Without it, a variant's cheaper row would
// win the selection and land on a cart line carrying no variantId. It is not
// a live bug fix; it is what makes the widening safe.
/**
 * Whether a supply row can back a cart line: active and in stock, both flags explicitly true
 * (absent on a pre-Bug-#302 payload, read as no). The one definition every reader of "sellable"
 * on the product page uses — this selection, the other-vendor list and the shopper's choice — so
 * none can offer a row another then refuses.
 */
export const isSellable = (vendor: ProductVendor): boolean =>
    vendor.isActive === true && vendor.inStock === true;

export const selectVendorSku = (
    vendors: ProductVendor[] | undefined,
    selectedVariantId: string | null,
): ProductVendor | undefined => {
    let lowest: ProductVendor | undefined;
    for (const vendor of vendorRowsForSku(vendors, selectedVariantId)) {
        if (!isSellable(vendor)) continue;
        if (!lowest || vendor.price < lowest.price) {
            lowest = vendor;
        }
    }
    return lowest;
};
